import { useAuthenticatedUser } from "src/contexts/AuthenticatedUserContext";

import React, { FC, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { FontAwesomeIcon } from '../../../constants/fontAwesomeIcons';
import { faMegaphone } from '@fortawesome/pro-duotone-svg-icons/faMegaphone';
import { useTheme } from '../../../contexts/ThemeContext';

import './FeedbackButton.scss';

const FeedbackButton: FC = () => {

    const { light } = useTheme();
    const { user } = useAuthenticatedUser();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const unsubscribeRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        const feedbackIntegration = Sentry.getFeedback();
        
        if (feedbackIntegration && buttonRef.current) {
            // Clean up previous attachment if it exists
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }
            
            // Attach feedback to button with current theme
            const unsubscribe = feedbackIntegration.attachTo(
                buttonRef.current, 
                { 
                    colorScheme: light ? 'light' : 'dark'
                }
            );
            
            unsubscribeRef.current = unsubscribe || null;
        }

        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [light, user]);

    const rippleEffect = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        let ripple = document.createElement("span");
        ripple.classList.add("ripple");
        event.currentTarget.appendChild(ripple);

        setTimeout(() => {
            ripple.remove();
        }, 300);
    };

    const handleOnClickEvent: React.MouseEventHandler<HTMLButtonElement> = (
        event
    ) => {
        rippleEffect(event);
    };

    return (
        <button
            className="feedback-button feedback-button-transition"
            onClick={handleOnClickEvent}
            ref={buttonRef}
            type="button"
            title="Report a Bug"
        >
            <FontAwesomeIcon 
                className="feedback-button-icon"
                icon={faMegaphone} 
                fixedWidth
            /> 
        </button>
    );
}

export default FeedbackButton;
